import type { Adres } from '@/types/domain';
import { adresRegel } from '@/utils/format';

/**
 * De huurwoningen van de woningcorporatie in dit plangebied.
 *
 * Waarom deze lijst in de app staat en niet in de database: of een woning
 * huur is, is een eigenschap van het pand en niet iets wat een coach aan de
 * deur ontdekt. De BAG bevat geen eigendomsgegevens, dus zonder deze lijst
 * zie je pas ná een bezoek of het huur is — precies te laat.
 *
 * Van de 76 woningen in het plangebied zijn dit er 50, dus twee derde. De
 * corporatie heeft laten weten dat zij deze woningen afkoppelt. Dat besluit
 * ligt daarmee niet bij de bewoner, en dat verandert het gesprek aan de deur:
 * daar gaat het dan over de tuin, niet over de regenpijp.
 *
 * Alle adressen zijn nagelopen in de BAG en bestaan.
 *
 * Ranonkelstraat 2b kwam er later bij. Die stond niet op de opgave van de
 * corporatie, maar een coach trof hem ter plekke aan en zette hem op huur.
 * Ranonkelstraat 2 en 2a bestaan als eigen adres in hetzelfde pand; die zijn
 * niet bevestigd en staan er dus niet bij.
 *
 * Ranonkelstraat 19a staat er ook niet bij, om dezelfde reden. Een driehoek
 * die er ten onrechte omheen staat laat een coach denken dat het al geregeld
 * is, en dan slaat hij een gesprek over dat wel gevoerd moest worden.
 *
 * Schrijfwijze zoals op de kaart: straatnaam, spatie, huisnummer.
 */
export const HUURADRESSEN: string[] = [
  // Ranonkelstraat, oneven 1 tot en met 27
  'Ranonkelstraat 1',
  'Ranonkelstraat 3',
  'Ranonkelstraat 5',
  'Ranonkelstraat 7',
  'Ranonkelstraat 9',
  'Ranonkelstraat 11',
  'Ranonkelstraat 13',
  'Ranonkelstraat 15',
  'Ranonkelstraat 17',
  'Ranonkelstraat 19',
  'Ranonkelstraat 21',
  'Ranonkelstraat 23',
  'Ranonkelstraat 25',
  'Ranonkelstraat 27',

  // Ranonkelstraat, even 4 tot en met 14, plus 2b
  'Ranonkelstraat 2b',
  'Ranonkelstraat 4',
  'Ranonkelstraat 6',
  'Ranonkelstraat 8',
  'Ranonkelstraat 10',
  'Ranonkelstraat 12',
  'Ranonkelstraat 14',

  // Kasteellaan
  'Kasteellaan 11',
  'Kasteellaan 13',
  'Kasteellaan 15',
  'Kasteellaan 17',
  'Kasteellaan 19',

  // Vijfhoevenlaan
  'Vijfhoevenlaan 46',
  'Vijfhoevenlaan 48',
  'Vijfhoevenlaan 50',
  'Vijfhoevenlaan 52',
  'Vijfhoevenlaan 54',
  'Vijfhoevenlaan 56',
  'Vijfhoevenlaan 58',
  'Vijfhoevenlaan 60',

  // Rijtselaan, even 22 tot en met 36
  'Rijtselaan 22',
  'Rijtselaan 24',
  'Rijtselaan 26',
  'Rijtselaan 28',
  'Rijtselaan 30',
  'Rijtselaan 32',
  'Rijtselaan 34',
  'Rijtselaan 36',

  // Rijtselaan, oneven 13 tot en met 27
  'Rijtselaan 13',
  'Rijtselaan 15',
  'Rijtselaan 17',
  'Rijtselaan 19',
  'Rijtselaan 21',
  'Rijtselaan 23',
  'Rijtselaan 25',
  'Rijtselaan 27',
];

const genormaliseerd = new Set(HUURADRESSEN.map((adres) => adres.trim().toLowerCase()));

/** Of dit een huurwoning van de corporatie is. */
export function isHuurwoning(adres: Adres): boolean {
  return genormaliseerd.has(adresRegel(adres).trim().toLowerCase());
}
